import { forwardRef, useState, useEffect, useRef, ReactNode, ReactElement, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export interface ComboBoxOption<T = unknown> {
  value: T;
  label: string;
  disabled?: boolean;
}

const comboBoxVariants = cva(
  'flex w-full items-center justify-between rounded-md border bg-background text-foreground px-3 py-2 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors shadow-input',
  {
    variants: {
      variant: {
        default: 'border-input focus:ring-2 focus:ring-ring focus:border-transparent',
        error: 'border-destructive focus:ring-2 focus:ring-destructive focus:border-transparent',
      },
      size: {
        default: 'h-10',
        sm: 'h-8 text-xs',
        lg: 'h-12',
      },
      searchable: {
        true: 'cursor-text',
        false: 'cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      searchable: false,
    },
  }
);

export interface ComboBoxProps<T = unknown> extends VariantProps<typeof comboBoxVariants> {
  value?: T | null;
  onChange: (value: T | null) => void;
  options: ComboBoxOption<T>[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  emptyMessage?: string;
  renderOption?: (option: ComboBoxOption<T>) => ReactNode;
  className?: string;
  dropdownFooter?: ReactNode;
  getOptionKey?: (option: T) => string | number;
  compareValues?: (a: T | null | undefined, b: T | null | undefined) => boolean;
}

function ComboBoxComponent<T = unknown>(
  {
    value,
    onChange,
    options,
    placeholder = 'Select an option...',
    error,
    disabled,
    loading,
    searchable = false,
    onSearch,
    searchQuery = '',
    emptyMessage = 'No options found',
    renderOption,
    className,
    dropdownFooter,
    variant,
    size,
    getOptionKey,
    compareValues,
    ...props
  }: ComboBoxProps<T>,
  _ref: React.ForwardedRef<HTMLDivElement>
) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Generate unique IDs for accessibility
  const comboboxId = useId();
  const listboxId = useId();
  const optionIdPrefix = useId();

  // Type guard to check if object has id property
  const hasIdProperty = (obj: unknown): obj is { id: unknown } => {
    return typeof obj === 'object' && obj !== null && 'id' in obj;
  };

  // Default comparison function for values
  const defaultCompareValues = (a: T | null | undefined, b: T | null | undefined): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;

    // For objects with 'id' property, compare by id
    if (hasIdProperty(a) && hasIdProperty(b)) {
      // Safely access id properties with null checks
      return a.id != null && b.id != null && a.id === b.id;
    }

    return false;
  };

  const compareFunction = compareValues || defaultCompareValues;

  // Find selected option
  const selectedOption = options.find(option => compareFunction(option.value, value));
  const displayValue = selectedOption?.label || '';

  // Use external search query if provided, otherwise use internal
  const currentSearchQuery = onSearch ? searchQuery : internalSearchQuery;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!onSearch) {
          setInternalSearchQuery('');
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearch]);

  const handleInputChange = (query: string) => {
    if (onSearch) {
      onSearch(query);
    } else {
      setInternalSearchQuery(query);
    }
    if (!isOpen) setIsOpen(true);
  };

  const handleSelectOption = (option: ComboBoxOption<T>) => {
    if (option.disabled) return;

    onChange(option.value);
    setIsOpen(false);
    if (!onSearch) {
      setInternalSearchQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const filteredOptions = onSearch
      ? options
      : options.filter(option =>
          option.label.toLowerCase().includes(internalSearchQuery.toLowerCase())
        );

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setFocusedOptionIndex(-1);
        if (!onSearch) {
          setInternalSearchQuery('');
        }
        inputRef.current?.blur();
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && focusedOptionIndex >= 0) {
          const selectedOption = filteredOptions[focusedOptionIndex];
          if (selectedOption && !selectedOption.disabled) {
            handleSelectOption(selectedOption);
          }
        } else {
          setIsOpen(true);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedOptionIndex(0);
        } else {
          const nextIndex = Math.min(focusedOptionIndex + 1, filteredOptions.length - 1);
          setFocusedOptionIndex(nextIndex);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = Math.max(focusedOptionIndex - 1, 0);
          setFocusedOptionIndex(prevIndex);
        }
        break;

      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setFocusedOptionIndex(0);
        }
        break;

      case 'End':
        if (isOpen) {
          e.preventDefault();
          setFocusedOptionIndex(filteredOptions.length - 1);
        }
        break;
    }
  };

  // Filter options for internal search
  const filteredOptions = onSearch
    ? options
    : options.filter(option =>
        option.label.toLowerCase().includes(internalSearchQuery.toLowerCase())
      );

  return (
    <div className={cn('relative w-full', className)} ref={dropdownRef} {...props}>
      {/* Input/Button */}
      <div className="relative">
        {searchable ? (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={
              focusedOptionIndex >= 0 ? `${optionIdPrefix}-${focusedOptionIndex}` : undefined
            }
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${comboboxId}-error` : undefined}
            className={cn(
              comboBoxVariants({
                variant: error ? 'error' : variant,
                size,
                searchable: true,
              }),
              'pr-10 placeholder:text-muted-foreground'
            )}
            placeholder={value ? displayValue : placeholder}
            value={isOpen ? currentSearchQuery : displayValue}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        ) : (
          <button
            type="button"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={
              focusedOptionIndex >= 0 ? `${optionIdPrefix}-${focusedOptionIndex}` : undefined
            }
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${comboboxId}-error` : undefined}
            className={cn(
              comboBoxVariants({
                variant: error ? 'error' : variant,
                size,
                searchable: false,
              }),
              'pr-10',
              !value && 'text-muted-foreground'
            )}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          >
            <span className="truncate">{displayValue || placeholder}</span>
          </button>
        )}

        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center pr-2"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-label={isOpen ? 'Close options list' : 'Open options list'}
          tabIndex={-1}
        >
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <p id={`${comboboxId}-error`} className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label="Options"
          className="absolute z-10 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div
              className="px-3 py-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              Loading...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground" role="status">
              {emptyMessage}
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = compareFunction(option.value, value);
              const optionKey = getOptionKey ? getOptionKey(option.value) : index;

              return (
                <div
                  key={optionKey}
                  id={`${optionIdPrefix}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none',
                    option.disabled && 'opacity-50 cursor-not-allowed',
                    isSelected && 'bg-primary/10 text-primary',
                    focusedOptionIndex === index && 'bg-accent text-accent-foreground'
                  )}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setFocusedOptionIndex(index)}
                >
                  {renderOption ? renderOption(option) : option.label}
                </div>
              );
            })
          )}
          {dropdownFooter && <div className="border-t border-border">{dropdownFooter}</div>}
        </div>
      )}
    </div>
  );
}

const ComboBox = forwardRef(ComboBoxComponent) as <T = unknown>(
  props: ComboBoxProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement | null;

export { ComboBox };
